import { useEffect, useState } from "react";
import { Save, Send, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { SettingsTabs } from "@/components/settings/SettingsTabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DEFAULTS = { enabled: false, host: "", port: 587, security: "starttls", username: "", from_email: "", from_name: "TSKPRO", has_password: false };

const F = ({ label, children, hint }) => (
  <div className="space-y-1.5"><Label className="text-slate-600">{label}</Label>{children}{hint && <p className="text-xs text-slate-400">{hint}</p>}</div>
);

export default function EmailSettings() {
  const { user } = useAuth();
  const [f, setF] = useState(DEFAULTS);
  const [password, setPassword] = useState("");
  const [testTo, setTestTo] = useState(user.email);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/email").then((r) => setF({ ...DEFAULTS, ...r.data }));
  }, []);

  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));

  const save = async () => {
    setBusy(true);
    try {
      const body = { enabled: f.enabled, host: f.host, port: Number(f.port), security: f.security, username: f.username, from_email: f.from_email, from_name: f.from_name };
      if (password) body.password = password;
      const { data } = await api.put("/settings/email", body);
      setF({ ...DEFAULTS, ...data });
      setPassword("");
      toast.success("Email settings saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    try {
      await api.post("/settings/email/test", { to: testTo });
      toast.success(`Test email sent to ${testTo}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title="Settings" subtitle="Connect your email server (SMTP) so TSKPRO can send password reset emails." />
      <SettingsTabs />
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6 max-w-5xl">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-5" data-testid="email-settings-form">
          <label className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-4 py-3">
            <span><span className="block font-bold text-slate-800">Email sending</span><span className="text-xs text-slate-500">Turn on once the details below are saved and tested.</span></span>
            <Switch checked={f.enabled} onCheckedChange={set("enabled")} data-testid="email-enabled-switch" />
          </label>
          <div className="grid sm:grid-cols-[1fr_120px] gap-4">
            <F label="SMTP server host" hint="e.g. smtp.gmail.com or smtp.office365.com"><Input value={f.host} onChange={set("host")} data-testid="email-host-input" /></F>
            <F label="Port"><Input type="number" value={f.port} onChange={set("port")} data-testid="email-port-input" /></F>
          </div>
          <F label="Security">
            <Select value={f.security} onValueChange={set("security")}>
              <SelectTrigger data-testid="email-security-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="starttls">STARTTLS (usually port 587)</SelectItem>
                <SelectItem value="ssl">SSL/TLS (usually port 465)</SelectItem>
                <SelectItem value="none">None (port 25)</SelectItem>
              </SelectContent>
            </Select>
          </F>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Username"><Input value={f.username} onChange={set("username")} autoComplete="off" data-testid="email-username-input" /></F>
            <F label="Password" hint={f.has_password ? "A password is saved. Leave empty to keep it." : "For Gmail/Outlook use an app password."}>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={f.has_password ? "••••••••" : ""} autoComplete="new-password" data-testid="email-password-input" />
            </F>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Sender email"><Input type="email" value={f.from_email} onChange={set("from_email")} data-testid="email-from-input" /></F>
            <F label="Sender name"><Input value={f.from_name} onChange={set("from_name")} data-testid="email-from-name-input" /></F>
          </div>
          <div className="flex justify-end"><Button className="rounded-full" onClick={save} disabled={busy} data-testid="email-save-btn"><Save className="w-4 h-4 mr-1.5" /> Save settings</Button></div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3 h-fit" data-testid="email-test-card">
          <div className="flex items-center gap-2 font-bold text-slate-800"><Mail className="w-4 h-4" /> Send a test email</div>
          <p className="text-xs text-slate-500">Uses the saved settings, even while sending is switched off.</p>
          <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="email-test-to-input" />
          <Button variant="outline" className="rounded-full w-full" onClick={test} disabled={busy || !testTo} data-testid="email-test-btn"><Send className="w-4 h-4 mr-1.5" /> Send test</Button>
        </div>
      </div>
    </div>
  );
}
